import React, { useEffect, useRef, useState } from 'react';
import { Ban, CircleAlert, TriangleAlert } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { shiftsAPI } from '../../services/api';
import { confirmDialog, notify } from '../../utils/notify';
import Modal from '../../components/Modal';
import { endDateFor } from './shiftDates';

// One shift: new (with the guard, site or day the grid was clicked on) or
// existing. While the form changes the server checks it: blocks (red) stop
// the save, warnings (amber) are for the planner to weigh.

/** A rule's result in the reader's language */
export const describe = (item, t) => {
  switch (item.code) {
    case 'overlap':
      return item.with
        ? t('Overlaps another shift of this guard: {site}, {from} – {to}', { site: item.with.site_name, from: item.with.starts_at.replace('T', ' '), to: item.with.ends_at.replace('T', ' ') })
        : t('Overlaps another shift of this guard');
    case 'employee_inactive': return t('This guard is not active');
    case 'site_inactive': return t('This site is not active');
    case 'client_inactive': return t('The client of this site is not active');
    case 'short_rest_before': return t('Only {hours} h of rest after the previous shift (minimum {minimum} h)', item);
    case 'short_rest_after': return t('Only {hours} h of rest before the next shift (minimum {minimum} h)', item);
    case 'weekly_hours': return t('{hours} h planned in the week of {week} (more than {maximum} h)', item);
    case 'leave_approved': return t('Approved leave from {from} to {to}', item);
    case 'employee_on_leave': return t('This guard is on leave');
    default: return item.code;
  }
};

const formFrom = (shift, defaults, template) => {
  if (shift) {
    return {
      employee_id: String(shift.employee_id), site_id: String(shift.site_id), template_id: shift.template_id ? String(shift.template_id) : '',
      start_date: shift.starts_at.slice(0, 10), start_time: shift.starts_at.slice(11, 16),
      end_date: shift.ends_at.slice(0, 10), end_time: shift.ends_at.slice(11, 16), notes: shift.notes || '',
    };
  }
  const startTime = template ? template.start_time : '06:00';
  const endTime = template ? template.end_time : '18:00';
  return {
    employee_id: defaults.employee_id ? String(defaults.employee_id) : '',
    site_id: defaults.site_id ? String(defaults.site_id) : '',
    template_id: template ? String(template.id) : '',
    start_date: defaults.date, start_time: startTime,
    end_date: endDateFor(defaults.date, startTime, endTime), end_time: endTime, notes: '',
  };
};

const ShiftDialog = ({ shift, defaults, guards, sites, templates, canSave, canCancel, onClose, onSaved }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState(() => formFrom(shift, defaults || {}, null));
  const [endTouched, setEndTouched] = useState(Boolean(shift));
  const [result, setResult] = useState({ blocks: [], warnings: [] });
  const [saving, setSaving] = useState(false);
  const seq = useRef(0);

  const payload = () => ({
    employee_id: Number(form.employee_id), site_id: Number(form.site_id),
    starts_at: `${form.start_date}T${form.start_time}`, ends_at: `${form.end_date}T${form.end_time}`,
    notes: form.notes, template_id: form.template_id ? Number(form.template_id) : null,
  });
  const complete = form.employee_id && form.site_id && form.start_date && form.start_time && form.end_date && form.end_time;

  // the server's check, a moment after the last change
  useEffect(() => {
    if (!complete) { setResult({ blocks: [], warnings: [] }); return undefined; }
    const mine = ++seq.current;
    const timer = setTimeout(() => {
      shiftsAPI.check({ ...payload(), id: shift ? shift.id : undefined })
        .then((res) => { if (mine === seq.current) setResult({ blocks: res.data.blocks, warnings: res.data.warnings }); })
        .catch(() => { if (mine === seq.current) setResult({ blocks: [], warnings: [] }); });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.employee_id, form.site_id, form.start_date, form.start_time, form.end_date, form.end_time]);

  const set = (field, value) => {
    const next = { ...form, [field]: value };
    // the end date follows the times until the planner sets it
    if (!endTouched && ['start_date', 'start_time', 'end_time'].includes(field)) next.end_date = endDateFor(next.start_date, next.start_time, next.end_time);
    setForm(next);
  };

  const applyTemplate = (id) => {
    const template = templates.find((x) => String(x.id) === id);
    if (!template) { setForm({ ...form, template_id: '' }); return; }
    setEndTouched(false);
    setForm({ ...form, template_id: id, start_time: template.start_time, end_time: template.end_time,
      end_date: endDateFor(form.start_date, template.start_time, template.end_time) });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const res = shift ? await shiftsAPI.update(shift.id, payload()) : await shiftsAPI.create(payload());
      notify(shift ? t('Shift updated') : t('Shift planned'), res.data.warnings && res.data.warnings.length ? 'warning' : 'success');
      onSaved();
    } catch (error) {
      const data = error.response?.data;
      if (data?.blocks) setResult({ blocks: data.blocks, warnings: data.warnings || [] });
      const fields = data?.errors ? Object.entries(data.errors).map(([k, v]) => `${k}: ${v}`).join('; ') : null;
      notify(fields || data?.message || t('Error saving the shift'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const cancelShift = async () => {
    if (!await confirmDialog(t('Cancel this shift? It stays in the history as cancelled.'))) return;
    try {
      setSaving(true);
      await shiftsAPI.cancel(shift.id);
      notify(t('Shift cancelled'), 'success');
      onSaved();
    } catch (error) {
      notify(error.response?.data?.message || t('Error cancelling the shift'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const activeSites = sites.filter((s) => s.active || String(s.id) === form.site_id);
  // a guard no longer active still shows on their own shift
  const guardList = shift && !guards.some((g) => g.id === shift.employee_id)
    ? [...guards, { id: shift.employee_id, first_name: shift.first_name, last_name: shift.last_name, employee_code: shift.employee_code }]
    : guards;
  const usableTemplates = templates.filter((x) => x.active && (!x.site_id || String(x.site_id) === form.site_id));

  return (
    <Modal
      open
      title={shift ? t('Edit Shift') : t('Plan a Shift')}
      onClose={onClose}
      busy={saving}
      size="md"
      footer={(
        <>
          {shift && canCancel && (
            <button type="button" className="btn btn--secondary sh-danger" onClick={cancelShift} disabled={saving}>
              <Ban aria-hidden="true" /> {t('Cancel shift')}
            </button>
          )}
          <button type="button" className="btn btn--secondary" onClick={onClose}>{t('Close')}</button>
          {canSave && (
            <button type="submit" form="shift-form" className="btn btn--primary" disabled={saving || !complete || result.blocks.length > 0}>
              {saving ? t('Saving...') : t('Save')}
            </button>
          )}
        </>
      )}
    >
      <form id="shift-form" className="sh-dialog" onSubmit={save}>
        <div className="sh-field">
          <label className="field-label" htmlFor="shift-guard">{t('Guard')}</label>
          <select id="shift-guard" className="select" value={form.employee_id} onChange={(e) => set('employee_id', e.target.value)} required disabled={!canSave}>
            <option value="">{t('Choose a guard')}</option>
            {guardList.map((g) => (
              <option key={g.id} value={g.id}>{g.first_name} {g.last_name}{g.employee_code ? ` (${g.employee_code})` : ''}</option>
            ))}
          </select>
        </div>
        <div className="sh-field">
          <label className="field-label" htmlFor="shift-site">{t('Site')}</label>
          <select id="shift-site" className="select" value={form.site_id} onChange={(e) => set('site_id', e.target.value)} required disabled={!canSave}>
            <option value="">{t('Choose a site')}</option>
            {activeSites.map((s) => <option key={s.id} value={s.id}>{s.client_name} – {s.name}</option>)}
          </select>
        </div>
        {usableTemplates.length > 0 && canSave && (
          <div className="sh-field">
            <label className="field-label" htmlFor="shift-template">{t('Template (optional)')}</label>
            <select id="shift-template" className="select" value={form.template_id} onChange={(e) => applyTemplate(e.target.value)}>
              <option value="">{t('No template')}</option>
              {usableTemplates.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.start_time}–{x.end_time})</option>)}
            </select>
          </div>
        )}
        <div className="sh-form-row">
          <div className="sh-field">
            <label className="field-label" htmlFor="shift-start-date">{t('Start')}</label>
            <input id="shift-start-date" className="input" type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} required disabled={!canSave} />
          </div>
          <div className="sh-field">
            <label className="field-label" htmlFor="shift-start-time">{t('Time')}</label>
            <input id="shift-start-time" className="input" type="time" value={form.start_time} onChange={(e) => set('start_time', e.target.value)} required disabled={!canSave} />
          </div>
        </div>
        <div className="sh-form-row">
          <div className="sh-field">
            <label className="field-label" htmlFor="shift-end-date">{t('End')}</label>
            <input id="shift-end-date" className="input" type="date" value={form.end_date}
              onChange={(e) => { setEndTouched(true); set('end_date', e.target.value); }} required disabled={!canSave} />
          </div>
          <div className="sh-field">
            <label className="field-label" htmlFor="shift-end-time">{t('Time')}</label>
            <input id="shift-end-time" className="input" type="time" value={form.end_time} onChange={(e) => set('end_time', e.target.value)} required disabled={!canSave} />
          </div>
        </div>
        <div className="sh-field">
          <label className="field-label" htmlFor="shift-notes">{t('Notes')}</label>
          <textarea id="shift-notes" className="textarea" rows="2" value={form.notes} onChange={(e) => set('notes', e.target.value)} disabled={!canSave} />
        </div>

        {(result.blocks.length > 0 || result.warnings.length > 0) && (
          <ul className="sh-rules" aria-live="polite">
            {result.blocks.map((b, i) => (
              <li key={`b${i}`} className="sh-rule is-block"><CircleAlert aria-hidden="true" /> {describe(b, t)}</li>
            ))}
            {result.warnings.map((w, i) => (
              <li key={`w${i}`} className="sh-rule is-warning"><TriangleAlert aria-hidden="true" /> {describe(w, t)}</li>
            ))}
          </ul>
        )}
      </form>
    </Modal>
  );
};

export default ShiftDialog;
